import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

export interface Notification {
  _id: string;
  userId: string;
  type: 'coaching' | 'friend_activity' | 'general';
  title: string;
  body: string;
  read: boolean;
  data?: any;
  priority: number;
  sentAt: string;
  readAt?: string;
  createdAt: string;
  updatedAt: string;
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingFriendRequests, setPendingFriendRequests] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [pushToken, setPushToken] = useState<string | null>(null);

  // Check if browser supports notifications
  const hasSupportForNotifications = 'Notification' in window && 'serviceWorker' in navigator;

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!hasSupportForNotifications) {
      console.warn('Notifications not supported in this browser');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setHasPermission(permission === 'granted');

      if (permission === 'granted') {
        await subscribeToPushNotifications();
      }

      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, [hasSupportForNotifications]);

  // Subscribe to push notifications
  const subscribeToPushNotifications = useCallback(async () => {
    if (!hasSupportForNotifications) {
      console.error('Browser does not support notifications');
      return null;
    }

    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/service-worker.js');

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Subscribe to push notifications
      const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapidPublicKey) {
        console.error('VAPID public key not found in environment variables');
        return null;
      }
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      // Send subscription to backend
      const token = JSON.stringify(subscription);
      const response = await apiService.registerPushToken(token);

      setPushToken(token);
      setHasPermission(true);

      return subscription;
    } catch (error) {
      console.error('❌ Error subscribing to push notifications:', error);
      console.error('Error details:', {
        name: (error as Error).name,
        message: (error as Error).message,
        stack: (error as Error).stack
      });
      return null;
    }
  }, [hasSupportForNotifications]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }

      await apiService.unregisterPushToken();
      setPushToken(null);
      setHasPermission(false);
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
    }
  }, []);

  // Fetch notification history
  const fetchNotifications = useCallback(async (limit: number = 50, unreadOnly: boolean = false) => {
    setLoading(true);
    try {
      const response = await apiService.getNotificationHistory(limit, unreadOnly);
      if (response.success) {
        setNotifications(response.notifications || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch unread count (includes pending friend requests)
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await apiService.getUnreadNotificationCount();
      if (response.success) {
        setUnreadCount(response.count || 0);
        setPendingFriendRequests(response.pendingFriendRequests || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const response = await apiService.markNotificationAsRead(notificationId);
      if (response.success) {
        setNotifications(prev =>
          prev.map(n => n._id === notificationId ? { ...n, read: true, readAt: new Date().toISOString() } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
        window.dispatchEvent(new Event('notificationCountChanged'));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await apiService.markAllNotificationsAsRead();
      if (response.success) {
        setNotifications(prev =>
          prev.map(n => ({ ...n, read: true, readAt: new Date().toISOString() }))
        );
        setUnreadCount(0);
        window.dispatchEvent(new Event('notificationCountChanged'));
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    try {
      const response = await apiService.sendTestNotification();
      if (response.success) {
        // Refresh notifications after a short delay to allow the notification to be created
        setTimeout(() => {
          fetchNotifications();
          fetchUnreadCount();
        }, 1000);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error sending test notification:', error);
      return false;
    }
  }, [fetchNotifications, fetchUnreadCount]);

  // Delete a single notification
  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      const response = await apiService.deleteNotification(notificationId);
      if (response.success) {
        const notification = notifications.find(n => n._id === notificationId);
        setNotifications(prev => prev.filter(n => n._id !== notificationId));
        // Decrement unread count if the deleted notification was unread
        if (notification && !notification.read) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
        window.dispatchEvent(new Event('notificationCountChanged'));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting notification:', error);
      return false;
    }
  }, [notifications]);

  // Delete all notifications
  const clearAllNotifications = useCallback(async () => {
    try {
      const response = await apiService.clearAllNotifications();
      if (response.success) {
        setNotifications([]);
        setUnreadCount(0);
        window.dispatchEvent(new Event('notificationCountChanged'));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      return false;
    }
  }, []);

  // Check permission status on mount and auto-revalidate push subscription
  useEffect(() => {
    if (!hasSupportForNotifications) return;

    const isGranted = Notification.permission === 'granted';
    setHasPermission(isGranted);

    // If permission is granted, validate the push subscription is still active
    // This fixes the issue where subscriptions silently expire and users stop
    // getting notifications until they manually toggle off/on
    if (isGranted) {
      (async () => {
        try {
          const registration = await navigator.serviceWorker.getRegistration('/service-worker.js');
          if (!registration) {
            // Service worker lost - re-register and re-subscribe
            await subscribeToPushNotifications();
            return;
          }

          const subscription = await registration.pushManager.getSubscription();
          if (!subscription) {
            // Subscription expired or was cleared - re-subscribe
            await subscribeToPushNotifications();
            return;
          }

          // Subscription exists - re-send to backend in case it was cleared server-side
          // (e.g., after a 410 error cleared the token). This is cheap and ensures sync.
          const lastRevalidation = localStorage.getItem('pushRevalidatedAt');
          const twelveHoursAgo = Date.now() - (12 * 60 * 60 * 1000);
          if (!lastRevalidation || parseInt(lastRevalidation, 10) < twelveHoursAgo) {
            const token = JSON.stringify(subscription);
            await apiService.registerPushToken(token);
            localStorage.setItem('pushRevalidatedAt', Date.now().toString());
            setPushToken(token);
          }
        } catch (error) {
          console.warn('Push subscription revalidation failed:', error);
        }
      })();
    }
  }, [hasSupportForNotifications, subscribeToPushNotifications]);

  // Fetch initial data, listen for cross-instance sync events, poll every 30s
  useEffect(() => {
    fetchUnreadCount();

    const handleCountChanged = () => fetchUnreadCount();
    window.addEventListener('notificationCountChanged', handleCountChanged);

    const pollInterval = setInterval(fetchUnreadCount, 30000);

    return () => {
      window.removeEventListener('notificationCountChanged', handleCountChanged);
      clearInterval(pollInterval);
    };
  }, [fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    pendingFriendRequests,
    loading,
    hasPermission,
    pushToken,
    hasSupportForNotifications,
    requestPermission,
    subscribeToPushNotifications,
    unsubscribe,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    sendTestNotification,
    deleteNotification,
    clearAllNotifications,
  };
};

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
